import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import "./index.css";

export default function StatCard({
    value,
    title,
    caption,
    icon,
    onClick,
    className = "",
    backgroundColor = "#e734a6",
    lista = [],
    openOn = "click", // "click" | "hover"
}) {
    const stringValue = String(value ?? "");
    const horas = String(stringValue).split("/")[0] ?? "";
    const totalFuncs = Array.isArray(lista) ? lista.length : 0;

    const [tipOpen, setTipOpen] = useState(false);
    const [panelPos, setPanelPos] = useState({ left: 0, top: 0 });
    const [hasDOM, setHasDOM] = useState(false); // SSR-safe

    const cardRef = useRef(null);
    const panelRef = useRef(null);

    // calcula posição ao lado do card (viewport)
    const computePos = (clientX, clientY) => {
        const pad = 12;
        const r = cardRef.current?.getBoundingClientRect();
        if (!r) return { left: pad, top: pad };

        let left = (clientX ?? r.right) + pad;
        let top = (clientY ?? r.top) + pad;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = panelRef.current?.offsetWidth ?? 480;
        const h = panelRef.current?.offsetHeight ?? 200;

        if (left + w + pad > vw) left = Math.max(pad, r.left - w - pad);
        if (top + h + pad > vh) top = Math.max(pad, vh - h - pad);

        return { left, top };
    };

    const openTip = (e) => {
        if (!Array.isArray(lista) || lista.length === 0) return;
        setTipOpen(true);
        // posiciona ao abrir
        setTimeout(() => setPanelPos(computePos(e?.clientX, e?.clientY)), 0);
    };

    // Fechar somente ao clicar fora (outside click) ou ESC
    useEffect(() => {
        if (!tipOpen) return;

        const onDown = (ev) => {
            const panel = panelRef.current;
            const card = cardRef.current;
            const t = ev.target;
            if (!panel || !card) return;
            // se clicou dentro do painel ou do card, permanece aberto
            if (panel.contains(t) || card.contains(t)) return;
            setTipOpen(false);
        };

        const onKey = (ev) => {
            if (ev.key === "Escape") setTipOpen(false);
        };

        document.addEventListener("mousedown", onDown, true);
        document.addEventListener("touchstart", onDown, true);
        window.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("mousedown", onDown, true);
            document.removeEventListener("touchstart", onDown, true);
            window.removeEventListener("keydown", onKey);
        };
    }, [tipOpen]);

    useEffect(() => setHasDOM(true), []);

    // handlers de abertura (escolha hover/click via prop)
    const hoverProps =
        openOn === "hover"
            ? {
                onMouseEnter: openTip,
                onFocus: openTip,
            }
            : {};

    const clickProps =
        openOn === "click"
            ? {
                 onMouseEnter: (e) => {
                    // toggle: clicar de novo no card fecha
                    if (tipOpen) setTipOpen(false);
                    else openTip(e);
                },
                onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (tipOpen) setTipOpen(false);
                        else openTip(e);
                    }
                },
            }
            : {};

    return (
        <div
            ref={cardRef}
            className={`stat-card ${onClick ? "is-clickable" : ""} ${className}`}
            role={onClick ? "button" : "group"}
            tabIndex={0}
            style={{ backgroundColor }}
            {...hoverProps}
            {...clickProps}
        >
            <div className="stat-card-content">
                <div className="stat-card-top">
                    <div className="stat-card-left">
                        <img src={`/images/${icon}.png`} className="iconPng" />
                    </div>
                    <div className="stat-card-right">
                        <div className="stat-card-value">{stringValue.split("/")[0]}</div>
                        {stringValue.split("/")[1] && (
                            <>
                                <span className="stat-card-separator">/</span>
                                <div className="stat-card-subvalue">
                                    {stringValue.split("/")[1]}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="stat-card-texts">
                <div className="stat-card-title">{title}</div>
                {caption && <div className="stat-card-caption">{caption}</div>}
            </div>
            <div className="stat-card-bar" />

            {/* Tooltip STICKY via portal — não fecha sozinho e pode ser maior */}
            {hasDOM && tipOpen && Array.isArray(lista) && lista.length > 0 &&
                createPortal(
                    <div
                        ref={panelRef}
                        role="dialog"
                        aria-label={`Detalhes de ${title}`}
                        style={{
                            position: "fixed",
                            left: "50%",
                            top: "120px",
                            zIndex: 2147483647,
                            background: "#fff",
                            borderRadius: 12,
                            boxShadow: "0 16px 40px rgba(16,24,40,0.18)",
                            border: "1px solid #EEF2F6",
                            padding: 16,
                            width: "min(260px, 96vw)", // pode ser bem maior que o card
                            maxHeight: "70vh",
                            overflowY: "auto",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "#101828", flex: 1 }}>
                                {title}
                            </div>
                            {/* botão opcional de fechar */}
                            <button
                                type="button"
                                onClick={() => setTipOpen(false)}
                                aria-label="Fechar"
                                style={{
                                    border: 0, background: "transparent", cursor: "pointer",
                                    fontSize: 18, lineHeight: 1, color: "#98A2B3"
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ color: "#667085", marginTop: 4, fontSize: 12 }}>
                            {totalFuncs} funcionário{totalFuncs === 1 ? "" : "s"}
                        </div>

                        <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0 }}>
                            {lista.map((f, idx) => (
                                <li
                                    key={f.id ?? `${f.nome ?? "func"}-${idx}`}
                                    title={f.nome}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "6px 0",
                                        fontSize: 12,
                                        color: "#101828",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        borderBottom: "1px solid #F2F4F7",
                                    }}
                                >
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {f.nome ?? "—"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body
                )
            }
        </div>
    );
}

StatCard.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    caption: PropTypes.string,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
    backgroundColor: PropTypes.string,
    lista: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            nome: PropTypes.string,
            data_nascimento: PropTypes.string,
        })
    ),
    openOn: PropTypes.oneOf(["click", "hover"]),
};
